import * as React from "react";
import {
  Input,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Badge,
} from "@repo/ui";
import { format } from "date-fns";
import { X, ChevronDown, Check } from "lucide-react";
import {
  InventorySearchCombobox,
  DateRangeFilter,
  PatientCombobox,
  type InventoryItem,
} from "@/components/forms";
import { cn } from "@repo/utils";
import { getAuthUsers } from "@/lib/api/auth";
import type { Patient } from "@/lib/types/patients";

export interface DispenseFilters {
  dateFrom?: string;
  dateTo?: string;
  userIds?: string;
  ndc?: string;
  itemName?: string;
  rxNumber?: string;
  patientId?: string;
  patientName?: string;
}

interface User {
  id: string;
  initials: string;
  full_name: string | null;
  role: string;
}

export interface DispenseFiltersProps {
  onFilterChange: (filters: DispenseFilters) => void;
  initialFilters?: DispenseFilters;
}

export function DispenseFilters({
  onFilterChange,
  initialFilters = {},
}: DispenseFiltersProps) {
  const [users, setUsers] = React.useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState(true);
  const [usersOpen, setUsersOpen] = React.useState(false);

  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>(
    initialFilters.userIds ? initialFilters.userIds.split(",") : []
  );
  const [selectedInventoryItem, setSelectedInventoryItem] =
    React.useState<InventoryItem | null>(null);
  const [selectedPatient, setSelectedPatient] = React.useState<Patient | null>(
    null
  );
  const [patientDisplayName, setPatientDisplayName] = React.useState<
    string | null
  >(initialFilters.patientName || null);
  const [rxNumber, setRxNumber] = React.useState(initialFilters.rxNumber || "");
  const [dateFrom, setDateFrom] = React.useState<Date | undefined>(
    initialFilters.dateFrom ? new Date(initialFilters.dateFrom) : undefined
  );
  const [dateTo, setDateTo] = React.useState<Date | undefined>(
    initialFilters.dateTo ? new Date(initialFilters.dateTo) : undefined
  );
  const dateRange = { from: dateFrom, to: dateTo };

  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getAuthUsers({ pageSize: 100 });
        setUsers(data.data?.users || data.users || []);
      } catch (error) {
        console.error("Failed to fetch users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };
    void fetchUsers();
  }, []);

  const buildFilters = React.useCallback(
    (
      overrides: Partial<{
        dateFrom: Date | undefined;
        dateTo: Date | undefined;
        userIds: string[];
        inventoryItem: InventoryItem | null;
        patient: Patient | null;
        rxNumber: string;
      }> = {}
    ): DispenseFilters => {
      const df = "dateFrom" in overrides ? overrides.dateFrom : dateFrom;
      const dt = "dateTo" in overrides ? overrides.dateTo : dateTo;
      const uids = overrides.userIds ?? selectedUserIds;
      const item =
        "inventoryItem" in overrides
          ? overrides.inventoryItem
          : selectedInventoryItem;
      const pat = "patient" in overrides ? overrides.patient : selectedPatient;
      const rx = overrides.rxNumber ?? rxNumber;

      // For patient, fall back to initialFilters when no Patient object is available
      const patId =
        pat?.id ||
        (!("patient" in overrides) ? initialFilters.patientId : undefined);
      const patName = pat
        ? [pat.first_name, pat.last_name].filter(Boolean).join(" ")
        : !("patient" in overrides)
          ? initialFilters.patientName
          : undefined;

      return {
        dateFrom: df ? format(df, "yyyy-MM-dd") : undefined,
        dateTo: dt ? format(dt, "yyyy-MM-dd") : undefined,
        userIds: uids.length > 0 ? uids.join(",") : undefined,
        ndc: item?.ndc || undefined,
        itemName: item?.itemName || undefined,
        rxNumber: rx || undefined,
        patientId: patId || undefined,
        patientName: patName || undefined,
      };
    },
    [
      dateFrom,
      dateTo,
      selectedUserIds,
      selectedInventoryItem,
      selectedPatient,
      rxNumber,
      initialFilters.patientId,
      initialFilters.patientName,
    ]
  );

  const activeFilterCount = [
    dateFrom,
    dateTo,
    selectedUserIds.length > 0,
    selectedInventoryItem,
    selectedPatient || patientDisplayName,
    rxNumber,
  ].filter(Boolean).length;

  const handleClearAll = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
    setSelectedUserIds([]);
    setSelectedInventoryItem(null);
    setSelectedPatient(null);
    setPatientDisplayName(null);
    setRxNumber("");
    onFilterChange({});
  };

  const toggleUser = (userId: string) => {
    const next = selectedUserIds.includes(userId)
      ? selectedUserIds.filter((id) => id !== userId)
      : [...selectedUserIds, userId];
    setSelectedUserIds(next);
    onFilterChange(buildFilters({ userIds: next }));
  };

  // Debounced RX number filter — skip on mount, only fire when user changes the value
  const rxMountedRef = React.useRef(false);
  React.useEffect(() => {
    if (!rxMountedRef.current) {
      rxMountedRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      onFilterChange(buildFilters({ rxNumber }));
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rxNumber]);

  const userLabel =
    selectedUserIds.length === 0
      ? "Users"
      : selectedUserIds.length === 1
        ? (users.find((u) => u.id === selectedUserIds[0])?.full_name ??
          users.find((u) => u.id === selectedUserIds[0])?.initials ??
          "1 user")
        : `${selectedUserIds.length} users`;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Date range */}
      <DateRangeFilter
        value={dateRange}
        onChange={({ from, to }) => {
          setDateFrom(from);
          setDateTo(to);
          onFilterChange(buildFilters({ dateFrom: from, dateTo: to }));
        }}
      />

      <div className="h-5 w-px bg-border" />

      {/* Users popover */}
      <Popover open={usersOpen} onOpenChange={setUsersOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-9 gap-1.5 font-normal",
              selectedUserIds.length > 0 && "border-primary/50 text-primary"
            )}
            disabled={isLoadingUsers}
          >
            {userLabel}
            {selectedUserIds.length > 0 && (
              <Badge
                variant="secondary"
                className="h-4 px-1 text-[10px] leading-none"
              >
                {selectedUserIds.length}
              </Badge>
            )}
            <ChevronDown className="h-3.5 w-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search users..." />
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    onSelect={() => toggleUser(user.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedUserIds.includes(user.id)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {user.full_name || user.initials}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Inventory item */}
      {selectedInventoryItem ? (
        <div className="flex h-9 items-center gap-1 rounded-md border border-input bg-background px-3 text-sm">
          <span className="max-w-[150px] truncate text-sm">
            {selectedInventoryItem.itemName}
          </span>
          <button
            type="button"
            onClick={() => {
              setSelectedInventoryItem(null);
              onFilterChange(buildFilters({ inventoryItem: null }));
            }}
            className="ml-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <InventorySearchCombobox
          onSelect={(item) => {
            setSelectedInventoryItem(item);
            onFilterChange(buildFilters({ inventoryItem: item }));
          }}
          placeholder="Product..."
          searchPlaceholder="Search product..."
          className="h-9 w-[180px] text-sm"
        />
      )}

      {/* Patient */}
      {selectedPatient || patientDisplayName ? (
        <div className="flex h-9 items-center gap-1 rounded-md border border-input bg-background px-3 text-sm">
          <span className="max-w-[150px] truncate text-sm">
            {selectedPatient
              ? [selectedPatient.first_name, selectedPatient.last_name]
                  .filter(Boolean)
                  .join(" ")
              : patientDisplayName}
          </span>
          <button
            type="button"
            onClick={() => {
              setSelectedPatient(null);
              setPatientDisplayName(null);
              onFilterChange(buildFilters({ patient: null }));
            }}
            className="ml-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <PatientCombobox
          onSelect={(patient) => {
            setSelectedPatient(patient);
            setPatientDisplayName(null);
            onFilterChange(buildFilters({ patient }));
          }}
          placeholder="Patient..."
          searchPlaceholder="Search patients..."
          className="h-9 w-[160px] text-sm"
        />
      )}

      {/* RX number */}
      <Input
        placeholder="RX #"
        value={rxNumber}
        onChange={(e) => setRxNumber(e.target.value)}
        className="h-9 w-[110px] font-mono text-sm"
      />

      {/* Clear all */}
      {activeFilterCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          className="h-9 px-2 text-muted-foreground hover:text-foreground"
        >
          Clear all
          <X className="ml-1 h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
